import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Globe, FileDigit, Shield, Zap, CheckCircle, Laptop, Cloud, Lock } from "lucide-react";
import { Button } from "../../components/ui/button";

const DematerialisationMarchesPublicsArticle = () => {
  return (
    <>
      <Helmet>
        <title>Dématérialisation des Marchés Publics : Guide Complet 2025 | Mon DCE Express</title>
        <meta name="description" content="Guide complet sur la dématérialisation des marchés publics : obligations légales, plateformes de dépôt, signature électronique, et bonnes pratiques pour répondre en ligne." />
        <meta name="keywords" content="dématérialisation marché public, plateforme marché public, signature électronique, PLACE, réponse électronique appel d'offres" />
        <link rel="canonical" href="https://www.dce-express.fr/blog/dematerialisation-marches-publics" />
      </Helmet>

      <article className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Header */}
        <header className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Link to="/blog" className="inline-flex items-center text-emerald-100 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au blog
            </Link>
            
            <div className="flex items-center gap-4 mb-4">
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                Guide Pratique
              </span>
              <span className="px-3 py-1 bg-white/20 rounded-full text-sm font-medium">
                Numérique
              </span>
            </div>
            
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Dématérialisation des Marchés Publics : Guide Complet 2025
            </h1>
            
            <p className="text-xl text-emerald-100 mb-6 leading-relaxed">
              Tout savoir sur la réponse électronique aux appels d'offres : obligations, 
              plateformes, signature électronique et bonnes pratiques.
            </p>
            
            <div className="flex flex-wrap items-center gap-6 text-sm text-emerald-100">
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Mis à jour 2025
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                12 min de lecture
              </span>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Depuis le 1er octobre 2018, la dématérialisation des marchés publics est obligatoire 
              pour tous les marchés supérieurs à 40 000 € HT. Ce guide vous accompagne dans la 
              maîtrise des outils numériques de la commande publique.
            </p>

            {/* Key Points */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-8 my-8">
              <h3 className="text-xl font-bold text-emerald-800 mb-4 flex items-center gap-2">
                <Zap className="w-6 h-6" />
                Points clés à retenir
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span className="text-emerald-900">Obligatoire pour les marchés ≥ 40 000 € HT</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span className="text-emerald-900">Signature électronique souvent requise</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span className="text-emerald-900">Dépôt sur profil acheteur uniquement</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span className="text-emerald-900">Formats de fichiers normalisés</span>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Globe className="w-7 h-7 text-emerald-600" />
              1. Les obligations légales
            </h2>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              Seuils d'application
            </h3>

            <div className="overflow-x-auto my-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-emerald-700 text-white">
                    <th className="p-4 text-left">Montant du marché</th>
                    <th className="p-4 text-center">Dématérialisation</th>
                    <th className="p-4 text-center">Signature électronique</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b bg-white">
                    <td className="p-4">Moins de 40 000 € HT</td>
                    <td className="p-4 text-center">Facultative</td>
                    <td className="p-4 text-center">Facultative</td>
                  </tr>
                  <tr className="border-b bg-emerald-50">
                    <td className="p-4">40 000 € à 90 000 € HT</td>
                    <td className="p-4 text-center text-emerald-600 font-bold">Obligatoire</td>
                    <td className="p-4 text-center">Selon DCE</td>
                  </tr>
                  <tr className="border-b bg-white">
                    <td className="p-4">Plus de 90 000 € HT</td>
                    <td className="p-4 text-center text-emerald-600 font-bold">Obligatoire</td>
                    <td className="p-4 text-center text-emerald-600 font-bold">Généralement obligatoire</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              Ce que dit la loi
            </h3>

            <div className="bg-slate-100 border-l-4 border-slate-500 p-6 my-6">
              <p className="text-slate-700 italic">
                "Les communications et les échanges d'informations effectués dans le cadre de la 
                procédure de passation d'un marché public sont réalisés par voie électronique."
              </p>
              <p className="text-sm text-slate-500 mt-2">
                — Article L. 2132-2 du Code de la commande publique
              </p>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Laptop className="w-7 h-7 text-blue-600" />
              2. Les plateformes de dématérialisation
            </h2>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              Principales plateformes
            </h3>

            <div className="grid md:grid-cols-2 gap-6 my-8">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 mb-3">PLACE (État)</h4>
                <p className="text-blue-900 text-sm mb-3">
                  Plateforme des achats de l'État, utilisée par les ministères et établissements publics nationaux.
                </p>
                <a href="https://www.marches-publics.gouv.fr" target="_blank" rel="noopener noreferrer" 
                   className="text-blue-600 hover:underline text-sm">
                  marches-publics.gouv.fr →
                </a>
              </div>
              
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                <h4 className="font-bold text-indigo-800 mb-3">AWS (Achat Web Service)</h4>
                <p className="text-indigo-900 text-sm mb-3">
                  Solution mutualisée utilisée par de nombreuses collectivités territoriales.
                </p>
                <a href="https://www.achatpublic.com" target="_blank" rel="noopener noreferrer" 
                   className="text-indigo-600 hover:underline text-sm">
                  achatpublic.com →
                </a>
              </div>
              
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <h4 className="font-bold text-purple-800 mb-3">Maximilien (Île-de-France)</h4>
                <p className="text-purple-900 text-sm mb-3">
                  Plateforme régionale pour les marchés publics en Île-de-France.
                </p>
                <a href="https://www.maximilien.fr" target="_blank" rel="noopener noreferrer" 
                   className="text-purple-600 hover:underline text-sm">
                  maximilien.fr →
                </a>
              </div>
              
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-6">
                <h4 className="font-bold text-teal-800 mb-3">Autres plateformes</h4>
                <ul className="text-teal-900 text-sm space-y-1">
                  <li>• E-Marchespublics</li>
                  <li>• Klekoon</li>
                  <li>• Atexo / Xmarches</li>
                  <li>• Dematis</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Lock className="w-7 h-7 text-violet-600" />
              3. La signature électronique
            </h2>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              Les différents niveaux de signature
            </h3>

            <div className="space-y-4 my-6">
              <div className="bg-slate-100 rounded-xl p-6">
                <h4 className="font-semibold text-slate-800 mb-2">Signature simple</h4>
                <p className="text-slate-600 text-sm">
                  Scan de signature manuscrite ou checkbox "J'accepte". 
                  <span className="text-amber-600 font-medium"> Rarement acceptée pour les marchés publics.</span>
                </p>
              </div>
              
              <div className="bg-violet-50 border border-violet-200 rounded-xl p-6">
                <h4 className="font-semibold text-violet-800 mb-2">Signature avancée</h4>
                <p className="text-violet-900 text-sm">
                  Certificat électronique lié à une personne physique. 
                  <span className="text-violet-600 font-medium"> Standard pour la plupart des marchés publics.</span>
                </p>
              </div>
              
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                <h4 className="font-semibold text-emerald-800 mb-2">Signature qualifiée</h4>
                <p className="text-emerald-900 text-sm">
                  Plus haut niveau de sécurité, équivalent légal à la signature manuscrite. 
                  <span className="text-emerald-600 font-medium"> Exigée pour certains marchés sensibles.</span>
                </p>
              </div>
            </div>

            <h3 className="text-xl font-semibold text-slate-800 mt-8 mb-4">
              Obtenir un certificat de signature
            </h3>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 my-6">
              <h4 className="font-semibold text-amber-800 mb-3">Prestataires agréés :</h4>
              <ul className="space-y-2 text-amber-900">
                <li>• <strong>ChamberSign</strong> (Chambres de Commerce)</li>
                <li>• <strong>Certinomis</strong></li>
                <li>• <strong>DocuSign</strong></li>
                <li>• <strong>Certigreffe</strong> (Greffes des tribunaux)</li>
                <li>• <strong>Yousign</strong></li>
              </ul>
              <p className="text-sm text-amber-700 mt-4">
                Coût : environ 100€ à 300€ par an selon le niveau de signature.
              </p>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <FileDigit className="w-7 h-7 text-cyan-600" />
              4. Formats de fichiers acceptés
            </h2>

            <div className="grid md:grid-cols-3 gap-6 my-8">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl mb-2">📄</div>
                <h4 className="font-semibold text-green-800">Documents</h4>
                <p className="text-green-700 text-sm mt-2">PDF, DOCX, ODT</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                <div className="text-3xl mb-2">📊</div>
                <h4 className="font-semibold text-blue-800">Tableaux</h4>
                <p className="text-blue-700 text-sm mt-2">XLSX, ODS, CSV</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 text-center">
                <div className="text-3xl mb-2">🖼️</div>
                <h4 className="font-semibold text-purple-800">Images</h4>
                <p className="text-purple-700 text-sm mt-2">JPEG, PNG (max 10 Mo)</p>
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-500 p-6 my-6">
              <h4 className="font-semibold text-red-800 mb-2">Formats à éviter :</h4>
              <ul className="space-y-1 text-red-900 text-sm">
                <li>❌ Fichiers exécutables (.exe, .bat)</li>
                <li>❌ Archives protégées par mot de passe</li>
                <li>❌ Fichiers de plus de 100 Mo (selon plateforme)</li>
                <li>❌ Formats propriétaires rares</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
              <Cloud className="w-7 h-7 text-sky-600" />
              5. Bonnes pratiques de dépôt
            </h2>

            <ol className="space-y-6 my-8">
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
                <div>
                  <strong className="text-slate-800">Créez votre compte en avance</strong>
                  <p className="text-slate-600 mt-1">
                    Inscrivez-vous sur les plateformes avant d'avoir besoin de répondre. 
                    La validation peut prendre plusieurs jours.
                  </p>
                </div>
              </li>
              
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
                <div>
                  <strong className="text-slate-800">Testez votre certificat de signature</strong>
                  <p className="text-slate-600 mt-1">
                    Vérifiez que votre certificat fonctionne sur la plateforme concernée 
                    avant le jour du dépôt.
                  </p>
                </div>
              </li>
              
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
                <div>
                  <strong className="text-slate-800">Déposez 48h avant la date limite</strong>
                  <p className="text-slate-600 mt-1">
                    Anticipez les problèmes techniques : serveur saturé, fichier trop lourd, 
                    problème de connexion...
                  </p>
                </div>
              </li>
              
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">4</span>
                <div>
                  <strong className="text-slate-800">Conservez l'accusé de réception</strong>
                  <p className="text-slate-600 mt-1">
                    L'accusé électronique horodaté est la preuve de votre dépôt. 
                    Archivez-le précieusement.
                  </p>
                </div>
              </li>
              
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 bg-sky-100 text-sky-700 rounded-full flex items-center justify-center font-bold flex-shrink-0">5</span>
                <div>
                  <strong className="text-slate-800">Vérifiez le contenu du pli</strong>
                  <p className="text-slate-600 mt-1">
                    Avant validation finale, contrôlez que tous les fichiers sont présents 
                    et correctement nommés.
                  </p>
                </div>
              </li>
            </ol>

            {/* Conclusion */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-2xl p-8 my-12">
              <h3 className="text-xl font-bold mb-4">À retenir</h3>
              <p className="text-slate-300 leading-relaxed">
                La dématérialisation est aujourd'hui incontournable pour accéder aux marchés publics. 
                Maîtriser les outils numériques (plateformes, signature électronique, formats de fichiers) 
                est devenu une compétence essentielle pour toute entreprise souhaitant répondre aux 
                appels d'offres publics.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-8 text-center text-white mt-12">
            <h3 className="text-2xl font-bold mb-4">
              Simplifiez vos réponses aux appels d'offres
            </h3>
            <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
              Mon DCE Express analyse automatiquement vos DCE et vous aide à préparer 
              vos réponses plus rapidement.
            </p>
            <Link to="/analyse-gratuite">
              <Button size="lg" className="bg-white text-emerald-600 hover:bg-emerald-50">
                Essayer gratuitement
              </Button>
            </Link>
          </div>
        </div>
      </article>
    </>
  );
};

export default DematerialisationMarchesPublicsArticle;
